import { createSignal } from "solid-js";

import { registerSW } from "virtual:pwa-register";

const [pendingUpdate, setPendingUpdate] = createSignal<() => void>();

export { pendingUpdate };

const SW_CHECK_INTERVAL_MS = 10 * 60 * 1000;
const FOCUSED_REFRESH_GRACE_MS = 30 * 1000;
const BUILD_CHECK_INTERVAL_MS = 10 * 60 * 1000;
const FORCE_RELOAD_GUARD_MS = 15 * 1000;
const FORCE_RELOAD_GUARD_KEY = "boopboop.force_reload_at";

let buildFingerprint: string | undefined;
let applyingUpdate = false;

function canForceReload() {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(FORCE_RELOAD_GUARD_KEY) ?? 0);
  if (now - last < FORCE_RELOAD_GUARD_MS) return false;
  sessionStorage.setItem(FORCE_RELOAD_GUARD_KEY, String(now));
  return true;
}

function forceReload(reason: string) {
  if (!canForceReload()) return;
  console.info(`[updates] forcing reload: ${reason}`);
  window.location.reload();
}

function makeBuildFingerprint(payload: unknown) {
  const build = (payload as { build?: Record<string, unknown> } | undefined)
    ?.build;
  if (!build) return undefined;

  const commit = String(build.commit_sha ?? "");
  const timestamp = String(build.timestamp ?? "");
  const semver = String(build.semver ?? "");
  const commitTimestamp = String(build.commit_timestamp ?? "");
  const joined = [commit, timestamp, semver, commitTimestamp]
    .filter(Boolean)
    .join("|");

  return joined || undefined;
}

async function fetchBuildFingerprint() {
  try {
    const response = await fetch(`${window.location.origin}/api`, {
      cache: "no-store",
      credentials: "omit",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) return undefined;
    const payload = (await response.json()) as unknown;
    return makeBuildFingerprint(payload);
  } catch {
    return undefined;
  }
}

function scheduleFocusedRefresh(applyNow: () => void) {
  const activelyUsing =
    document.visibilityState === "visible" && document.hasFocus();

  if (!activelyUsing) {
    applyNow();
    return;
  }

  const timer = window.setTimeout(() => {
    cleanup();
    applyNow();
  }, FOCUSED_REFRESH_GRACE_MS);

  const onVisibility = () => {
    if (document.visibilityState !== "visible") {
      clearTimeout(timer);
      cleanup();
      applyNow();
    }
  };

  const onBlur = () => {
    clearTimeout(timer);
    cleanup();
    applyNow();
  };

  const cleanup = () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("blur", onBlur);
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("blur", onBlur);
}

if (import.meta.env.PROD) {
  const updateSW = registerSW({
    onNeedRefresh() {
      const applyAndReload = () => {
        if (applyingUpdate) return;
        applyingUpdate = true;
        void updateSW(true);

        window.setTimeout(() => {
          forceReload("service-worker-update");
        }, 1200);
      };

      setPendingUpdate(() => () => applyAndReload());
      scheduleFocusedRefresh(applyAndReload);
    },
    onOfflineReady() {
      console.info("Ready to work offline =)");
      // toast to users
    },
    onRegistered(r) {
      const checkForSwUpdate = () => r?.update();

      // Check for SW updates every 10 minutes and when user returns to tab.
      setInterval(checkForSwUpdate, SW_CHECK_INTERVAL_MS);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          checkForSwUpdate();
        }
      });

      // Backend build fingerprint check fallback for stale tabs/sessions.
      const checkBuildFingerprint = async () => {
        const latest = await fetchBuildFingerprint();
        if (!latest) return;

        if (!buildFingerprint) {
          buildFingerprint = latest;
          return;
        }

        if (latest !== buildFingerprint) {
          forceReload("backend-build-fingerprint-changed");
        }
      };

      void checkBuildFingerprint();
      setInterval(checkBuildFingerprint, BUILD_CHECK_INTERVAL_MS);
      window.addEventListener("focus", () => {
        void checkBuildFingerprint();
      });
    },
  });
}
